import { useMemo, useState } from 'react'
import { CHANNEL_LABEL, type Brief, type Channel } from 'brief/buildBrief'
import { formatWhen, relativeDay } from 'brief/parseWhen'
import { EmptyState, InlineLoader } from 'components/common/Loader'

/**
 * One story, in order. The per-channel tabs stay for their detail (recordings, document actions);
 * this is for reading what happened with the customer without assembling it from five tables.
 * It renders what the brief already loaded - opening it costs no request.
 */
export function TimelinePanel({ brief, loading }: { brief: Brief | null; loading: boolean }) {
  const [only, setOnly] = useState<Channel | 'all'>('all')
  const now = useMemo(() => new Date(), [])

  const counts = useMemo(() => {
    const map = new Map<Channel, number>()
    for (const item of brief?.timeline ?? []) map.set(item.channel, (map.get(item.channel) ?? 0) + 1)
    return map
  }, [brief])

  if (loading || !brief) return <InlineLoader />
  if (brief.timeline.length === 0) return <EmptyState message="Nothing has happened on this record yet." />

  const shown = only === 'all' ? brief.timeline : brief.timeline.filter((t) => t.channel === only)

  return (
    <>
      <div className="timeline-filters" role="tablist" aria-label="Filter the timeline">
        <button type="button" className={`timeline-chip${only === 'all' ? ' is-on' : ''}`} onClick={() => setOnly('all')}>
          Everything · {brief.timeline.length}
        </button>
        {[...counts.entries()].map(([channel, count]) => (
          <button
            key={channel}
            type="button"
            className={`timeline-chip${only === channel ? ' is-on' : ''}`}
            onClick={() => setOnly(channel)}
          >
            {CHANNEL_LABEL[channel]} · {count}
          </button>
        ))}
      </div>

      <ol className="timeline-list">
        {shown.map((item) => (
          <li key={item.id} className={`timeline-item ch-${item.channel}${item.direction === 'in' ? ' is-inbound' : ''}`}>
            <span className="timeline-when">
              {item.when ? (
                <>
                  <strong>{relativeDay(item.when, now)}</strong>
                  {formatWhen(item.when, item.hasTime)}
                </>
              ) : (
                <>{item.whenText || 'Date not given'}</>
              )}
            </span>
            <div className="timeline-body">
              <span className="timeline-title">
                <span className="timeline-channel">{CHANNEL_LABEL[item.channel]}</span>
                {item.title && item.title !== CHANNEL_LABEL[item.channel] ? item.title : ''}
              </span>
              {item.body && <p>{item.body}</p>}
              {item.who && <span className="activity-meta">{item.who}</span>}
            </div>
          </li>
        ))}
      </ol>

      {brief.unavailable.length > 0 && (
        <p className="brief-unavailable">Not shown because they could not be read: {brief.unavailable.join(', ')}.</p>
      )}
    </>
  )
}
